import React, { useState, useEffect } from 'react';
import { TextField, IconButton, InputAdornment, Tooltip } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useNotification } from '../contexts/NotificationContext';

const VoiceTextField = ({ onVoiceTranscript, ...props }) => {
    const [isListening, setIsListening] = useState(false);
    const [recognition, setRecognition] = useState(null);
    const { showNotification } = useNotification();

    useEffect(() => {
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            const rec = new SpeechRecognition();
            rec.continuous = false;
            rec.interimResults = false;
            rec.lang = 'en-IN';

            rec.onstart = () => {
                setIsListening(true);
            };

            rec.onend = () => {
                setIsListening(false);
            };

            rec.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                if (onVoiceTranscript) {
                    onVoiceTranscript(transcript);
                } else if (props.onChange && props.name) {
                    // Fallback if no specific handler, attempt to trigger standard onChange
                    const syntheticEvent = {
                        target: {
                            name: props.name,
                            value: (props.value || '') + (props.value ? ' ' : '') + transcript
                        }
                    };
                    props.onChange(syntheticEvent);
                }
            };

            rec.onerror = (event) => {
                console.error('Speech recognition error', event.error);
                showNotification('error', `Speech Error: ${event.error}`);
                setIsListening(false);
            };

            setRecognition(rec);
        }
    }, [props.name, props.onChange, props.value, onVoiceTranscript, showNotification]);

    const toggleListening = () => {
        if (isListening) {
            recognition.stop();
        } else {
            if (recognition) {
                recognition.start();
            } else {
                showNotification('error', 'Speech recognition not supported in this browser.');
            }
        }
    };

    return (
        <TextField
            {...props}
            InputProps={{
                ...props.InputProps,
                endAdornment: (
                    <InputAdornment position="end">
                        <Tooltip title={isListening ? "Listening..." : "Click to Speak"}>
                            <IconButton
                                onClick={toggleListening}
                                color={isListening ? "secondary" : "default"}
                                size="small"
                                sx={{
                                    animation: isListening ? 'pulse 1.5s infinite' : 'none',
                                    '@keyframes pulse': {
                                        '0%': { transform: 'scale(1)', opacity: 1 },
                                        '50%': { transform: 'scale(1.2)', opacity: 0.7 },
                                        '100%': { transform: 'scale(1)', opacity: 1 },
                                    }
                                }}
                            >
                                {isListening ? <MicIcon color="error" /> : <MicIcon />}
                            </IconButton>
                        </Tooltip>
                        {props.InputProps?.endAdornment}
                    </InputAdornment>
                ),
            }}
        />
    );
};

export default VoiceTextField;
